import React, { useCallback, useEffect, useRef, useState } from 'react';
import { serviceFeeApi } from '../../api/serviceFeeApi';
import { parseContractAmountField } from '../../utils/contractAmount.util';
import { formatVndInput } from '../../utils/vndFormat';

export type CasualParkingVarianceType = 'DEDUCTION' | 'VOUCHER';

export type CasualParkingVarianceLineForm = {
  varianceType: CasualParkingVarianceType;
  counterpartyName: string;
  amount: string;
  notes: string;
  contractApartmentId: string;
  contractPartnerKey: string;
};

type VoucherContractTarget = {
  contractApartmentId: string;
  contractCode: string;
  apartmentCode: string;
  customerName: string | null;
};

type VoucherPartnerOption = {
  optionKey: string;
  contractCode: string;
  counterpartyName: string;
  displayLabel: string;
  targets: VoucherContractTarget[];
};

export const emptyCasualParkingVarianceLine = (): CasualParkingVarianceLineForm => ({
  varianceType: 'VOUCHER',
  counterpartyName: '',
  amount: '',
  notes: '',
  contractApartmentId: '',
  contractPartnerKey: '',
});

export function buildCasualParkingVariancePayloadLines(lines: CasualParkingVarianceLineForm[]) {
  return lines
    .map((line) => ({
      varianceType: line.varianceType,
      counterpartyName: line.counterpartyName.trim(),
      amount: parseContractAmountField(line.amount),
      notes: line.notes.trim() || undefined,
      contractApartmentId:
        line.varianceType === 'VOUCHER' && line.contractApartmentId
          ? line.contractApartmentId
          : undefined,
    }))
    .filter((line) => line.amount > 0 && line.counterpartyName);
}

export function validateCasualParkingVariancePartnerSelection(
  lines: CasualParkingVarianceLineForm[],
  voucherPartnerOptions: VoucherPartnerOption[],
): string | null {
  for (const line of lines) {
    if (line.varianceType !== 'VOUCHER' || !line.contractPartnerKey) continue;
    const partner = voucherPartnerOptions.find((p) => p.optionKey === line.contractPartnerKey);
    if (partner && partner.targets.length === 0) {
      return `HĐ ${partner.contractCode} chưa liên kết căn hộ — mở Sửa hợp đồng thu và chọn căn trước khi ghi voucher`;
    }
    if (partner && partner.targets.length > 1 && !line.contractApartmentId) {
      return `Chọn căn HĐ thu cho ${partner.counterpartyName}`;
    }
  }
  return null;
}

interface CasualParkingVarianceLinesEditorProps {
  buildingId: string;
  billingPeriod: string;
  varianceLines: CasualParkingVarianceLineForm[];
  setVarianceLines: React.Dispatch<React.SetStateAction<CasualParkingVarianceLineForm[]>>;
  expectedVariance: number;
  varianceRemaining: number;
  active?: boolean;
}

export const CasualParkingVarianceLinesEditor: React.FC<CasualParkingVarianceLinesEditorProps> = ({
  buildingId,
  billingPeriod,
  varianceLines,
  setVarianceLines,
  expectedVariance,
  varianceRemaining,
  active = true,
}) => {
  const [contractTargetsByLine, setContractTargetsByLine] = useState<
    Record<number, VoucherContractTarget[]>
  >({});
  const targetFetchTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const [voucherPartnerOptions, setVoucherPartnerOptions] = useState<VoucherPartnerOption[]>([]);

  const applyVoucherPartner = useCallback(
    (index: number, optionKey: string) => {
      const option = voucherPartnerOptions.find((row) => row.optionKey === optionKey);
      if (!option) {
        setVarianceLines((prev) =>
          prev.map((line, i) =>
            i === index ? { ...line, contractPartnerKey: '', contractApartmentId: '' } : line,
          ),
        );
        setContractTargetsByLine((prev) => ({ ...prev, [index]: [] }));
        return;
      }
      setContractTargetsByLine((prev) => ({ ...prev, [index]: option.targets }));
      setVarianceLines((prev) =>
        prev.map((line, i) =>
          i === index
            ? {
                ...line,
                contractPartnerKey: option.optionKey,
                counterpartyName: option.counterpartyName,
                contractApartmentId:
                  option.targets.length === 1 ? option.targets[0].contractApartmentId : '',
              }
            : line,
        ),
      );
    },
    [setVarianceLines, voucherPartnerOptions],
  );

  const refreshVoucherContractTargets = useCallback(
    async (index: number, counterpartyName: string) => {
      const name = counterpartyName.trim();
      if (!buildingId || !name) {
        setContractTargetsByLine((prev) => ({ ...prev, [index]: [] }));
        return;
      }
      try {
        const { targets } = await serviceFeeApi.getCasualParkingVoucherContractTargets(
          buildingId,
          name,
        );
        const mapped = targets.map((t) => ({
          contractApartmentId: t.contractApartmentId,
          contractCode: t.contractCode,
          apartmentCode: t.apartmentCode,
          customerName: t.customerName,
        }));
        setContractTargetsByLine((prev) => ({ ...prev, [index]: mapped }));
        if (mapped.length === 1) {
          setVarianceLines((prev) =>
            prev.map((line, i) =>
              i === index ? { ...line, contractApartmentId: mapped[0].contractApartmentId } : line,
            ),
          );
        }
      } catch {
        setContractTargetsByLine((prev) => ({ ...prev, [index]: [] }));
      }
    },
    [buildingId, setVarianceLines],
  );

  const scheduleContractTargetLookup = (index: number, counterpartyName: string) => {
    const existing = targetFetchTimers.current[index];
    if (existing) clearTimeout(existing);
    targetFetchTimers.current[index] = setTimeout(() => {
      refreshVoucherContractTargets(index, counterpartyName);
    }, 350);
  };

  useEffect(() => {
    if (!active || !buildingId) return;
    serviceFeeApi
      .getCasualParkingVoucherContractTargets(buildingId, '')
      .then((res) => {
        const partners = (res.partners ?? []).map((p) => ({
          optionKey: p.optionKey,
          contractCode:
            p.targets[0]?.contractCode ?? p.displayLabel.split(' · ')[1]?.trim() ?? '',
          counterpartyName: p.counterpartyName,
          displayLabel: p.displayLabel,
          targets: p.targets.map((t) => ({
            contractApartmentId: t.contractApartmentId,
            contractCode: t.contractCode,
            apartmentCode: t.apartmentCode,
            customerName: t.customerName,
          })),
        }));
        setVoucherPartnerOptions(partners);
      })
      .catch(() => setVoucherPartnerOptions([]));
  }, [active, buildingId]);

  useEffect(() => {
    if (!active || !buildingId || varianceLines.length === 0) return;
    varianceLines.forEach((line, index) => {
      if (line.varianceType === 'VOUCHER' && line.counterpartyName.trim()) {
        void refreshVoucherContractTargets(index, line.counterpartyName);
      }
    });
  }, [active, buildingId, varianceLines.length, refreshVoucherContractTargets]);

  const updateLine = (index: number, patch: Partial<CasualParkingVarianceLineForm>) => {
    setVarianceLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  };

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-3 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-amber-900">
          Chênh lệch (máy − thực thu): {formatVndInput(String(expectedVariance))}đ
        </p>
        <p
          className={`text-sm ${varianceRemaining === 0 ? 'text-emerald-700' : 'text-red-600'}`}
        >
          Còn phân bổ: {formatVndInput(String(varianceRemaining))}đ
        </p>
      </div>
      {varianceLines.map((line, index) => (
        <div
          key={index}
          className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start bg-white rounded border p-2"
        >
          <select
            className="md:col-span-3 border rounded px-2 py-1.5 text-sm"
            value={line.varianceType}
            onChange={(e) => {
              const varianceType = e.target.value as CasualParkingVarianceType;
              updateLine(index, {
                varianceType,
                contractApartmentId: '',
                contractPartnerKey: '',
              });
              if (varianceType === 'VOUCHER') {
                scheduleContractTargetLookup(index, line.counterpartyName);
              }
            }}
          >
            <option value="VOUCHER">Voucher</option>
            <option value="DEDUCTION">Khấu trừ phí</option>
          </select>
          {line.varianceType === 'VOUCHER' && voucherPartnerOptions.length > 0 ? (
            <select
              className="md:col-span-12 border rounded px-2 py-1.5 text-sm bg-white"
              value={line.contractPartnerKey}
              onChange={(e) => applyVoucherPartner(index, e.target.value)}
            >
              <option value="">Chọn khách HĐ thu T-VL001 (Thu sau)</option>
              {voucherPartnerOptions.map((partner) => (
                <option key={partner.optionKey} value={partner.optionKey}>
                  {partner.displayLabel}
                </option>
              ))}
            </select>
          ) : null}
          <input
            className="md:col-span-4 border rounded px-2 py-1.5 text-sm"
            placeholder={
              line.varianceType === 'VOUCHER'
                ? 'Hoặc gõ đối tượng (gợi ý theo tên KH)'
                : 'Đối tượng (công ty / lý do)'
            }
            list={
              line.varianceType === 'VOUCHER' && voucherPartnerOptions.length > 0
                ? `voucher-partner-suggest-${index}`
                : undefined
            }
            value={line.counterpartyName}
            onChange={(e) => {
              const value = e.target.value;
              updateLine(index, {
                counterpartyName: value,
                contractApartmentId: '',
                contractPartnerKey: '',
              });
              if (line.varianceType === 'VOUCHER') {
                scheduleContractTargetLookup(index, value);
              }
            }}
            onBlur={() => {
              if (line.varianceType === 'VOUCHER') {
                refreshVoucherContractTargets(index, line.counterpartyName);
              }
            }}
          />
          {line.varianceType === 'VOUCHER' && voucherPartnerOptions.length > 0 ? (
            <datalist id={`voucher-partner-suggest-${index}`}>
              {voucherPartnerOptions.map((partner) => (
                <option
                  key={partner.optionKey}
                  value={partner.counterpartyName}
                  label={partner.displayLabel}
                />
              ))}
            </datalist>
          ) : null}
          <input
            className="md:col-span-3 border rounded px-2 py-1.5 text-sm"
            inputMode="numeric"
            placeholder="Số tiền"
            value={line.amount}
            onChange={(e) => updateLine(index, { amount: formatVndInput(e.target.value) })}
          />
          <button
            type="button"
            className="md:col-span-2 text-sm text-red-600 hover:text-red-800"
            onClick={() => setVarianceLines((prev) => prev.filter((_, i) => i !== index))}
          >
            Xóa
          </button>
          <input
            className="md:col-span-12 border rounded px-2 py-1.5 text-sm"
            placeholder="Ghi chú dòng (tùy chọn)"
            value={line.notes}
            onChange={(e) => updateLine(index, { notes: e.target.value })}
          />
          {line.varianceType === 'VOUCHER' ? (
            <div className="md:col-span-12 text-xs text-gray-600">
              {(() => {
                const targets = contractTargetsByLine[index] ?? [];
                if (!line.counterpartyName.trim()) {
                  return 'HĐ thu T-VL001 (Thu sau): chọn khách ở dropdown hoặc gõ một phần tên (vd. VNV → VNVC)';
                }
                if (targets.length === 0) {
                  return 'Không có HĐ thu T-VL001 (Thu sau) khớp đối tượng — chỉ ghi voucher, không tạo HĐ thu';
                }
                if (targets.length === 1) {
                  const t = targets[0];
                  return (
                    <span className="text-emerald-800">
                      HĐ thu: {t.contractCode} · căn {t.apartmentCode}
                      {t.customerName ? ` (${t.customerName})` : ''} — tự động cộng vào kỳ{' '}
                      {billingPeriod}
                    </span>
                  );
                }
                return (
                  <label className="flex flex-wrap items-center gap-2">
                    <span>Chọn căn HĐ thu T-VL001:</span>
                    <select
                      className="border rounded px-2 py-1 text-sm min-w-[200px]"
                      value={line.contractApartmentId}
                      onChange={(e) => updateLine(index, { contractApartmentId: e.target.value })}
                    >
                      <option value="">— Chọn căn —</option>
                      {targets.map((t) => (
                        <option key={t.contractApartmentId} value={t.contractApartmentId}>
                          {t.contractCode} · {t.apartmentCode}
                        </option>
                      ))}
                    </select>
                  </label>
                );
              })()}
            </div>
          ) : null}
        </div>
      ))}
      <button
        type="button"
        className="text-sm text-emerald-700 hover:text-emerald-900"
        onClick={() => setVarianceLines((prev) => [...prev, emptyCasualParkingVarianceLine()])}
      >
        + Thêm dòng
      </button>
    </div>
  );
};
